/**
 * As animações do template que dependem de rolagem.
 *
 * No original elas são interações do Webflow (IX2). O que sobrou no CSS
 * publicado foi só o estado inicial — por isso metade dos elementos do HTML do
 * template carrega um `style="opacity:0"` cru: sem o runtime, a página ficaria
 * com seções invisíveis para sempre.
 *
 * Aqui o mesmo efeito sai do GSAP, e com uma diferença que importa: o estado
 * inicial é aplicado *pelo* GSAP (`gsap.from`), não escrito no HTML. Se o
 * JavaScript falhar, quebrar ou for bloqueado, a página aparece inteira em vez
 * de aparecer vazia. Opacidade zero no HTML é uma aposta de que o script vai
 * rodar, e é uma aposta que a gente perde justamente nas conexões ruins.
 *
 * O vocabulário de movimento é o mesmo do resto do produto (`lib/motion.ts`):
 * uma curva assinatura e três durações. O template não declara as dele em lugar
 * nenhum acessível, então adotamos as nossas — elas fazem a home parecer o
 * mesmo produto que o painel, que é o ponto de ter identidade de movimento.
 */

import { useEffect, type RefObject } from 'react';
import { gsap, useGSAP, ScrollTrigger, EASE, DURATION } from '@/lib/motion';

/** Distância que os elementos percorrem ao entrar. */
const SUBIDA = 32;

/**
 * Mantém os gatilhos de rolagem em dia enquanto a página cresce.
 *
 * O ScrollTrigger calcula, na criação, em qual posição de rolagem cada seção
 * começa a animar — e guarda esse número. Numa página que nasce pronta isso
 * basta. Nesta aqui não: quando o visitante chega, metade das seções ainda não
 * existe (elas dependem de campeonatos, times e partidas que vêm da API), e a
 * página cresce de uns 6.000 para 12.000 pixels quando os dados chegam.
 *
 * O efeito é cruel de diagnosticar. As posições guardadas apontam para lugares
 * que mudaram de sítio, e as seções mais abaixo têm o gatilho num ponto que a
 * rolagem nunca alcança. Elas ficam paradas no estado inicial da animação —
 * `opacity: 0` — para sempre. O layout está correto, os elementos existem, têm
 * o tamanho certo, e a tela está em branco.
 *
 * Um `ResizeObserver` no corpo do documento resolve na raiz: toda vez que a
 * altura da página muda, os gatilhos são recalculados. Vale para dados que
 * chegam, para imagens que carregam e para a fonte que troca de métrica — três
 * coisas que acontecem depois do primeiro render e que nenhum evento de janela
 * anuncia.
 */
export function useGatilhosSempreEmDia() {
  useEffect(() => {
    let agendado = 0;

    const recalcular = () => {
      /*
       * Uma passada por quadro, no máximo. Sem isso, uma seção que entra
       * dispara o observador, que recalcula, o que muda a altura, o que dispara
       * o observador de novo — e o navegador passa a gastar o quadro inteiro
       * medindo em vez de rolar.
       */
      if (agendado) return;
      agendado = requestAnimationFrame(() => {
        agendado = 0;
        ScrollTrigger.refresh();
      });
    };

    const observador = new ResizeObserver(recalcular);
    observador.observe(document.body);
    window.addEventListener('load', recalcular);

    return () => {
      observador.disconnect();
      window.removeEventListener('load', recalcular);
      if (agendado) cancelAnimationFrame(agendado);
    };
  }, []);
}

interface Opcoes {
  /** Intervalo entre os elementos da mesma cena. */
  stagger?: number;
  /** Onde a seção começa a animar, na linguagem do ScrollTrigger. */
  start?: string;
  /** Recalcula quando estes valores mudam — use os ids dos dados da seção. */
  dependencias?: unknown[];
}

/**
 * Entrada padrão de uma seção: os elementos marcados sobem e materializam
 * quando a seção entra em cena, uma vez só.
 *
 * `once: true` não é economia: uma seção que reanima toda vez que volta para a
 * tela transforma a rolagem para cima num desfile de coisas piscando, e o
 * leitor perde a referência de onde estava. A entrada é um evento de chegada.
 *
 * @param raiz   a seção inteira — o gatilho e o escopo do seletor
 * @param alvos  seletores dentro dela, na ordem em que devem entrar
 */
export function useEntradaDaSecao(
  raiz: RefObject<HTMLElement | null>,
  alvos: string,
  { stagger = 0.09, start = 'top 85%', dependencias = [] }: Opcoes = {},
) {
  useGSAP(
    () => {
      const elementos = gsap.utils.toArray<HTMLElement>(alvos, raiz.current);
      if (elementos.length === 0) return;

      /*
       * `fromTo` com `immediateRender: false`, e não `from`.
       *
       * O `from` aplica o estado inicial na hora de criar o tween e conta com o
       * gatilho para desfazê-lo. Se o gatilho não disparar, o estado inicial é o
       * estado final: a seção fica em `opacity: 0` para sempre. E ele deixa de
       * disparar num caso que acontece toda hora aqui — quando o tween é criado
       * com a página já rolada além do ponto de entrada, `onEnter` nunca ocorre,
       * porque não houve travessia; com `once: true`, nunca mais vai ocorrer.
       * Foi exatamente assim que a seção de planos passou a renderizar em
       * branco: layout correto, elementos no tamanho certo, tela vazia.
       *
       * Com `immediateRender: false` o elemento nasce visível e só é escondido
       * no instante em que a animação começa. O pior caso deixa de ser "sumiu"
       * e passa a ser "apareceu sem animar", que é o pior caso aceitável.
       */
      gsap.fromTo(
        elementos,
        { y: SUBIDA, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: DURATION.hero,
          ease: EASE.signature,
          stagger,
          immediateRender: false,
          scrollTrigger: { trigger: raiz.current, start, once: true },
        },
      );

      return () => {
        ScrollTrigger.getAll()
          .filter((t) => t.trigger === raiz.current)
          .forEach((t) => t.kill());
      };
    },
    { scope: raiz, dependencies: dependencias, revertOnUpdate: true },
  );
}

/**
 * Entrada da hero, que roda na abertura em vez de por rolagem.
 *
 * A hero já está na tela quando a página carrega; esperar rolagem para animá-la
 * significaria que quem chega e não rola vê uma tela parada. É a única seção da
 * página com essa característica, e por isso a única com timeline própria.
 */
export function useEntradaDaHero(raiz: RefObject<HTMLElement | null>) {
  useGSAP(
    () => {
      const linha = gsap.timeline({ defaults: { ease: EASE.signature } });

      linha
        .from('[data-entra="etiqueta"]', { y: 16, autoAlpha: 0, duration: 0.6 })
        .from(
          '[data-entra="titulo"]',
          { yPercent: 108, duration: DURATION.hero },
          '-=0.3',
        )
        .from('[data-entra="texto"]', { y: 16, autoAlpha: 0, duration: 0.7 }, '-=0.55')
        .from(
          '[data-entra="acoes"]',
          { y: 14, autoAlpha: 0, stagger: 0.08, duration: 0.6 },
          '-=0.45',
        );
    },
    { scope: raiz },
  );
}

/**
 * A fileira de escudos que atravessa a seção "sobre".
 *
 * No template é uma faixa de logos de clube que deriva devagar para o lado.
 * Roda em laço infinito e não tem gatilho de rolagem: é textura de fundo, não
 * um evento. `xPercent` em vez de `left` porque só o primeiro não obriga o
 * navegador a refazer o layout a cada quadro.
 */
export function useDerivaDosEscudos(raiz: RefObject<HTMLElement | null>) {
  useGSAP(
    () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      gsap.to('[data-deriva]', {
        xPercent: -4,
        duration: 9,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    },
    { scope: raiz },
  );
}
